import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: '首頁', path: '/', matchPaths: ['/'] },
    { label: '產品系列', path: '/products', matchPaths: ['/products', '/product/'] },
    { label: '聽力知識', path: '/knowledge', matchPaths: ['/knowledge', '/article/'] },
    { label: '常見問題', path: '/faq', matchPaths: ['/faq'] },
    { label: '關於我們', path: '/about', matchPaths: ['/about'] },
    { label: '共聽生活協會', path: '/association', matchPaths: ['/association'] },
    { label: '預約檢測', path: '/booking', matchPaths: ['/booking', '/booking-success'] },
  ];

  const isActive = (link: typeof navLinks[0]) => {
    return link.matchPaths.some((p) => {
      if (p === '/') return location.pathname === '/';
      return location.pathname.startsWith(p);
    });
  };

  const navBase =
    'fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md';
  const navBg =
    scrolled || !isHomePage
      ? 'bg-white/80 shadow-sm'
      : 'bg-white/40';

  return (
    <nav className={`${navBase} ${navBg}`}>
      <div className="w-full px-4 md:px-10 flex items-center justify-between h-14 md:h-20">
        <a
          href="/"
          className="flex items-center"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          <span className="flex text-lg md:text-2xl font-bold tracking-wide whitespace-nowrap">
            <span className="text-[#1B3D2A]">富聆聽</span>
            <span className="text-[#D4A574]">健康</span>
          </span>
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const active = isActive(link);
            return (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.path);
                }}
                className={`relative transition-colors whitespace-nowrap ${
                  active
                    ? 'text-lg font-bold text-[#1B3D2A]'
                    : 'text-base font-medium text-[#5A6B5A] hover:text-[#1B3D2A]'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-[#D4A574] transition-all duration-300 ease-out ${
                    active ? 'w-3/4 opacity-100' : 'w-0 opacity-0'
                  }`}
                />
              </a>
            );
          })}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <button
            className="bg-[#1B3D2A] text-white px-6 py-3 rounded-full text-base font-medium hover:bg-[#143026] transition-colors whitespace-nowrap"
            onClick={() => navigate('/booking')}
          >
            立即預約
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-[#5A6B5A]"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-[#E8E0D6] px-4 py-4 shadow-lg max-h-[calc(100vh-3.5rem)] overflow-y-auto">
          {navLinks.map((link, index) => {
            const active = isActive(link);
            return (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.path);
                  setMobileMenuOpen(false);
                }}
                className={`relative block py-3 pl-3 transition-all duration-300 ${
                  index !== navLinks.length - 1 ? 'border-b border-[#E8E0D6]' : ''
                } ${
                  active
                    ? 'text-lg font-bold text-[#1B3D2A] border-l-[3px] border-l-[#D4A574] bg-[#1B3D2A]/5'
                    : 'text-base font-medium text-[#5A6B5A] hover:text-[#1B3D2A] border-l-[3px] border-l-transparent'
                }`}
              >
                {link.label}
              </a>
            );
          })}
          <button
            className="mt-4 w-full bg-[#1B3D2A] text-white px-6 py-3 rounded-full text-base font-medium hover:bg-[#143026] transition-colors"
            onClick={() => {
              navigate('/booking');
              setMobileMenuOpen(false);
            }}
          >
            立即預約
          </button>
        </div>
      )}
    </nav>
  );
}